import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import clsx from 'clsx'

import Btn from '../../../../../../UI/Btn'
import SelectField from '../../../../../../UI/SelectField'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'

import styles from '../style.module.scss'
// Derselbe Schalter wie bei „Upload durch die Lehrkraft" — ein Ein und Aus
// soll in den Einstellungen überall gleich aussehen.
import config from '../RequirementsConfig/style.module.scss'
// Die Beschreibungstexte dieses Abschnitts nutzen die ganze Kartenbreite.
import breit from './style.module.scss'

/*
 * Das Skills4Retail-Konzept.
 *
 * Hier steht alles, was Junior Achievement Austria daran steuern kann: ob es
 * überhaupt verlangt wird, was ohne bewertetes Konzept nicht mehr geht — je
 * Unternehmensform getrennt —, wie viele richtige Antworten ein Kapitel der
 * Lernstrecke verlangt und nach wie vielen Tagen an eine ausstehende Bewertung
 * erinnert wird.
 *
 * Die Werte müssen zu services/concept/index.js im Backend passen: Dort werden
 * sie ein zweites Mal gelesen und auf brauchbare Bereiche geholt.
 */

const REMINDER_MIN_DAYS = 3
const REMINDER_MAX_DAYS = 30

/*
 * Die drei Zustände je Unternehmensform. „Vor der Aktivierung" trifft die
 * Junior Company am härtesten — ohne Aktivierung gibt es keine
 * Mitarbeiter:innen, keine Anteile, keinen Geschäftsbetrieb. „Vor dem ersten
 * Finanzbericht" lässt die Company arbeiten und hält nur den Bericht zurück.
 */
const GATES = [
	{ value: 'OFF', label: 'aus — gilt für diese Unternehmensform nicht' },
	{ value: 'ACTIVATION', label: 'vor der Aktivierung (steht dann in der Checkliste)' },
	{ value: 'FIRST_REPORT', label: 'vor dem ersten Finanzbericht (Halbjahresbericht)' }
]

/*
 * Wer die Lernstrecke abschließen muss, bevor das Konzept zur Bewertung geht.
 * Bis 25.08.2026 war das fest verdrahtet: alle. „Alle" bleibt die Vorgabe —
 * eine leere oder kaputte Einstellung darf die Voraussetzung nicht
 * stillschweigend abschalten.
 */
const COURSE_MODES = [
	{ value: 'ALL', label: 'alle Mitarbeiter:innen' },
	{ value: 'PERCENT', label: 'ein Anteil (Prozent)' },
	{ value: 'COUNT', label: 'eine feste Anzahl Personen' },
	{ value: 'NONE', label: 'niemand — die Lernstrecke ist keine Voraussetzung' }
]

const PASS_CHOICES = [
	{ value: 3, label: '3 von 5 richtigen Antworten' },
	{ value: 4, label: '4 von 5 richtigen Antworten' },
	{ value: 5, label: '5 von 5 richtigen Antworten' }
]

const COMPANY_TYPES = [
	{ key: 'JC', label: 'Junior Company' },
	{ key: 'JCC', label: 'Junior Compact Company' },
	{ key: 'JBC', label: 'Junior Basic Company' }
]

const DEFAULTS = {
	active: false,
	gates: { JC: 'OFF', JCC: 'OFF', JBC: 'OFF' },
	quizPass: 4,
	reminderDays: 7,
	courseRequirement: { JC: { mode: 'ALL' }, JCC: { mode: 'ALL' }, JBC: { mode: 'ALL' } }
}

const gateSchema = Joi.string()
	.valid(...GATES.map(entry => entry.value))
	.required()
	.messages({ 'any.only': 'Bitte einen der drei Zustände auswählen.' })

/*
 * Die Zahl gilt nur bei „Anteil" bzw. „feste Anzahl" — bei „alle" und „niemand"
 * gibt es nichts einzutragen, und ein Pflichtfeld dort ließe sich nicht
 * speichern. `when` hängt die Prüfung deshalb an den gewählten Modus.
 */
const courseSchema = Joi.object().keys({
	mode: Joi.string()
		.valid(...COURSE_MODES.map(entry => entry.value))
		.required()
		.messages({ 'any.only': 'Bitte eine der vier Möglichkeiten auswählen.' }),
	value: Joi.number()
		.integer()
		.when('mode', {
			is: 'PERCENT',
			then: Joi.number().integer().min(1).max(100).required().messages({
				'number.base': 'Bitte einen Prozentwert zwischen 1 und 100 angeben.',
				'number.min': 'Weniger als 1 % lässt sich nicht einstellen.',
				'number.max': 'Mehr als 100 % gibt es nicht — dafür gibt es „alle".',
				'any.required': 'Bitte einen Prozentwert zwischen 1 und 100 angeben.'
			})
		})
		.when('mode', {
			is: 'COUNT',
			then: Joi.number().integer().min(1).max(99).required().messages({
				'number.base': 'Bitte eine Anzahl zwischen 1 und 99 angeben.',
				'number.min': 'Mindestens eine Person — für „keine" gibt es die vierte Möglichkeit.',
				'number.max': 'Mehr als 99 Personen hat keine Junior Company.',
				'any.required': 'Bitte eine Anzahl zwischen 1 und 99 angeben.'
			})
		})
		.optional()
		.allow('', null)
})

const schema = Joi.object({
	conceptRequirement: Joi.object().keys({
		active: Joi.boolean(),
		gates: Joi.object().keys({ JC: gateSchema, JCC: gateSchema, JBC: gateSchema }),
		courseRequirement: Joi.object().keys({ JC: courseSchema, JCC: courseSchema, JBC: courseSchema }),
		quizPass: Joi.number()
			.valid(3, 4, 5)
			.required()
			.messages({ 'any.only': 'Möglich sind nur 3, 4 oder 5 richtige Antworten.' }),
		/*
		 * Meldungen auf Deutsch und feldbezogen. Ein leeres Feld ist
		 * ausdrücklich ein Fehler: `Number("")` ist 0 und würde sonst
		 * stillschweigend auf den Mindestwert rutschen — dann erinnert die
		 * Anwendung plötzlich alle drei Tage.
		 */
		reminderDays: Joi.number()
			.integer()
			.min(REMINDER_MIN_DAYS)
			.max(REMINDER_MAX_DAYS)
			.required()
			.messages({
				'number.base': `Bitte eine Zahl zwischen ${REMINDER_MIN_DAYS} und ${REMINDER_MAX_DAYS} angeben.`,
				'number.integer': 'Bitte eine ganze Zahl von Tagen angeben.',
				'number.min': `Häufiger als alle ${REMINDER_MIN_DAYS} Tage wird nicht erinnert.`,
				'number.max': `Seltener als alle ${REMINDER_MAX_DAYS} Tage wird nicht erinnert.`,
				'any.required': `Bitte eine Zahl zwischen ${REMINDER_MIN_DAYS} und ${REMINDER_MAX_DAYS} angeben.`
			})
	})
})

export default function ConceptRequirement(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		control,
		watch,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const aktiv = watch('conceptRequirement.active')
	// Das Zahlenfeld gehört nur zu „Anteil" und „feste Anzahl" — bei „alle" und
	// „niemand" gibt es nichts einzutragen.
	const modi = watch('conceptRequirement.courseRequirement')

	const onSubmit = form => {
		const value = form.conceptRequirement

		props
			.updateOptions({
				name: 'conceptRequirement',
				value: {
					active: !!value.active,
					gates: {
						JC: value.gates?.JC || 'OFF',
						JCC: value.gates?.JCC || 'OFF',
						JBC: value.gates?.JBC || 'OFF'
					},
					courseRequirement: Object.fromEntries(
						COMPANY_TYPES.map(type => {
							const eintrag = value.courseRequirement?.[type.key] || {}
							const mode = eintrag.mode || 'ALL'
							// Ohne Zahl speichern, wo keine gilt: Ein stehen
							// gebliebener Wert von einem früheren Modus wäre
							// beim nächsten Umschalten eine Überraschung.
							return [
								type.key,
								mode === 'PERCENT' || mode === 'COUNT'
									? { mode, value: Number(eintrag.value) }
									: { mode }
							]
						})
					),
					quizPass: Number(value.quizPass),
					reminderDays: Number(value.reminderDays)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = props.form?.conceptRequirement || {}
		reset({
			conceptRequirement: {
				...DEFAULTS,
				...value,
				gates: { ...DEFAULTS.gates, ...(value.gates || {}) },
				courseRequirement: {
					...DEFAULTS.courseRequirement,
					...(value.courseRequirement || {})
				}
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="conceptRequirement" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Skills4Retail-Konzept</h3>
				<SettingHint
					className={breit.breit}
					example="Beispiel: Junior Company und Junior Compact Company vor dem ersten Finanzbericht, Junior Basic Company ausgenommen, 4 von 5 richtigen Antworten, Erinnerung nach 7 Tagen."
					warning="Die Einstellung wirkt nicht rückwirkend: Wer bereits aktiviert ist bzw. den ersten Finanzbericht schon abgegeben hat, wird nicht nachträglich angehalten. Ausgeschaltet sieht niemand etwas vom Konzept."
				>
					Die Schüler:innen erfassen ihr E-Commerce- und Digital-Marketing-Konzept in fünfzehn Feldern und
					ordnen sich den Feldern zu, an denen sie mitgearbeitet haben; daneben arbeiten sie eine Lernstrecke
					aus zehn Kapiteln durch. Ist alles beisammen, schließen sie ab; die betreuende Lehrkraft bewertet
					jedes Feld mit 0 bis 10 Punkten. Solange nicht alle fünfzehn Felder bewertet sind, geht es nicht
					weiter — was genau nicht weitergeht, legen Sie hier je Unternehmensform fest.
				</SettingHint>

				<div className={styles.sectionContent}>
					<label className={clsx(config.switch, styles.field)}>
						<input type="checkbox" {...register('conceptRequirement.active')} />
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>{aktiv ? 'Konzept verlangt' : 'ausgeschaltet'}</span>
					</label>

					{!aktiv && (
						<p className={clsx(styles.hintOff, breit.breit)}>
							Ausgeschaltet. Die Einstellungen darunter bleiben gespeichert, wirken aber nicht —
							Schüler:innen und Lehrkräfte sehen nichts vom Konzept.
						</p>
					)}

					<h4 className={styles.subTitle}>Was ohne bewertetes Konzept nicht geht — je Unternehmensform</h4>
					<ul className={clsx(styles.factList, breit.breit)}>
						<li>
							<b>aus</b> — das Konzept wird für diese Unternehmensform nicht verlangt und hält nichts
							zurück. Lernstrecke und Konzept bleiben sichtbar und benutzbar; was geschrieben wird, bleibt
							gespeichert.
						</li>
						<li>
							<b>vor der Aktivierung</b> — die Junior Company wird ohne vollständig bewertetes Konzept gar
							nicht erst freigeschaltet: keine Mitarbeiter:innen, keine Anteile, kein Geschäftsbetrieb.
							Die Voraussetzung steht als eigene Zeile in der Aktivierungs-Checkliste.
						</li>
						<li>
							<b>vor dem ersten Finanzbericht</b> — die Junior Company arbeitet normal, kann aber ihren
							Halbjahresbericht nicht abgeben. Bei Compact und Basic Company ist das deren einziger
							Bericht.
						</li>
					</ul>

					<div className={clsx(styles.flexContainer, styles.fieldsEven)}>
						{COMPANY_TYPES.map(type => (
							<SelectField
								key={type.key}
								className={styles.field}
								label={type.label}
								placeholder={t('Please select')}
								options={GATES}
								control={control}
								name={`conceptRequirement.gates.${type.key}`}
								error={errors.conceptRequirement?.gates?.[type.key]}
							/>
						))}
					</div>

					<h4 className={styles.subTitle}>Wer muss die Lernstrecke abschließen? — je Unternehmensform</h4>
					<p className={clsx(styles.subHint, breit.breit)}>
						Bevor eine Junior Company ihr Konzept zur Bewertung an die Lehrkraft übergeben kann, muss eine
						bestimmte Zahl ihrer Mitarbeiter:innen die zehn Kapitel der Lernstrecke durchgearbeitet und die
						Quizfragen bestanden haben — wie viele, legen Sie hier fest. <b>Alle</b> war bis August 2026
						fest eingestellt. <b>Niemand</b> heißt: Die Lernstrecke steht weiter offen und wird auch
						angezeigt — sie hält das Konzept nur nicht mehr zurück. Ein <b>Anteil</b> wird immer
						aufgerundet: Bei drei Personen und 50 % sind es zwei. Verlangt eine <b>feste Anzahl</b> mehr
						Personen, als die Junior Company Mitarbeiter:innen hat, gilt deren Zahl — so bleibt keine Firma
						an einer unerfüllbaren Vorgabe hängen. Wen die betreuende Lehrkraft begründet befreit hat, zählt
						als erfüllt.
					</p>

					{/*
					 * Dieselbe Reihe wie bei den Sperren darüber: drei Unternehmensformen
					 * nebeneinander. Das Zahlenfeld von „Anteil" und „feste Anzahl" steht
					 * unter seinem Auswahlfeld in derselben Spalte — untereinander gelistet
					 * sah dieselbe Frage dreimal anders aus als eine Zeile höher.
					 */}
					<div className={clsx(styles.flexContainer, styles.fieldsEven, breit.spaltenOben)}>
						{COMPANY_TYPES.map(type => {
							const mode = modi?.[type.key]?.mode
							const zahl = mode === 'PERCENT' || mode === 'COUNT'

							return (
								<div key={type.key} className={breit.spalte}>
									<SelectField
										className={styles.field}
										label={type.label}
										placeholder={t('Please select')}
										options={COURSE_MODES}
										control={control}
										name={`conceptRequirement.courseRequirement.${type.key}.mode`}
										error={errors.conceptRequirement?.courseRequirement?.[type.key]?.mode}
									/>
									{zahl && (
										<TextField
											className={styles.field}
											label={
												mode === 'PERCENT'
													? 'Anteil in Prozent (1–100)'
													: 'Anzahl Personen (1–99)'
											}
											register={register(
												`conceptRequirement.courseRequirement.${type.key}.value`
											)}
											error={errors.conceptRequirement?.courseRequirement?.[type.key]?.value}
										/>
									)}
								</div>
							)
						})}
					</div>

					<h4 className={styles.subTitle}>Lernstrecke und Erinnerung</h4>

					<div className={styles.flexContainer}>
						<SelectField
							className={styles.field}
							label="Ein Kapitel gilt als bestanden ab"
							placeholder={t('Please select')}
							options={PASS_CHOICES}
							control={control}
							name="conceptRequirement.quizPass"
							error={errors.conceptRequirement?.quizPass}
						/>
						<TextField
							className={styles.field}
							label="Lehrkraft an die offene Bewertung erinnern nach … Tagen"
							register={register('conceptRequirement.reminderDays')}
							error={errors.conceptRequirement?.reminderDays}
						/>
					</div>
					<p className={clsx(styles.subHint, breit.breit)}>
						<b>Die Hürde</b> gilt für alle zehn Kapitel gleich; jedes Kapitel hat fünf Quizfragen.
						Wiederholen ist erlaubt, gezählt wird immer der beste Versuch.
					</p>
					<p className={clsx(styles.subHint, breit.breit)}>
						<b>Die Erinnerung</b> betrifft die <b>betreuende Lehrkraft</b>, nicht die Schüler:innen: Hat
						eine Junior Company ihr Konzept zur Bewertung abgeschlossen und liegt es danach so viele Tage
						unbewertet, bekommt die Lehrkraft eine Mitteilung und eine E-Mail. Gezählt wird ab dem
						Abschließen; bleibt die Bewertung weiter aus, wird im selben Abstand erneut erinnert. Sobald die
						Bewertung abgeschlossen ist, endet die Erinnerung. Möglich sind {REMINDER_MIN_DAYS} bis{' '}
						{REMINDER_MAX_DAYS} Tage.
					</p>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

ConceptRequirement.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
